import {
  SpendCapExceeded,
  type LifecycleEvent,
  type LifecycleObserver,
} from './chain';
import type { BrandConfig, PeriodSpec } from '../branding/types';
import {
  DEFAULT_DAILY_PERIOD,
  DEFAULT_MONTHLY_PERIOD,
  DEFAULT_REFUSAL_MESSAGE,
} from '../branding/defaults';
import { renderTemplate } from '../branding/apply';
import { SpendStore, SpendCapsState } from '../storage/spend-store';

/**
 * Built-in spend-tracker observer. Reads persisted spend state and
 * subscribes to two lifecycle events:
 *
 *   spend.preflight             - veto if a dispatch would exceed the cap
 *   executor.dispatch.completed - record actuals after the dispatch
 *
 * Caps are reloadable in-process: `<brand> caps set` writes the new caps
 * to disk, and the next preflight reads them inside the spend-store mutex
 * (no restart needed). `reload()` is exposed for tests and explicit cache
 * busts.
 *
 * Throws `SpendCapExceeded` (from `@whittlelabs/jobs`) on veto; Sofia's
 * subscribe loop translates that to `refused_over_cap` on the wire.
 */
export interface SpendTrackerHandle extends LifecycleObserver {
  reload(): Promise<void>;
  currentCaps(): SpendCapsState;
  usage(now?: Date): { daily: UsageSnapshot; monthly: UsageSnapshot };
}

export interface UsageSnapshot {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  costUsd: number;
}

export function createSpendTrackerObserver(
  brand: BrandConfig,
  spendStateFile: string,
): SpendTrackerHandle {
  const store = new SpendStore(spendStateFile);
  const fallbackCaps: SpendCapsState = {
    dailyTokens: brand.defaultCaps.dailyTokens,
    monthlyTokens: brand.defaultCaps.monthlyTokens,
    dailyUsd: brand.defaultCaps.dailyUsd,
    monthlyUsd: brand.defaultCaps.monthlyUsd,
  };

  let caps: SpendCapsState = store.read(fallbackCaps).caps;

  const dailyPeriod: PeriodSpec =
    brand.caps?.periodDefinitions?.daily ?? DEFAULT_DAILY_PERIOD;
  const monthlyPeriod: PeriodSpec =
    brand.caps?.periodDefinitions?.monthly ?? DEFAULT_MONTHLY_PERIOD;
  const refusalTemplate = brand.caps?.refusalMessage ?? DEFAULT_REFUSAL_MESSAGE;

  function checkPreflight(
    snapshot: { daily: UsageSnapshot; monthly: UsageSnapshot },
    estimate: { inputTokens?: number; outputTokens?: number; costUsd?: number },
  ): void {
    const estTokens = (estimate.inputTokens ?? 0) + (estimate.outputTokens ?? 0);
    const estUsd = estimate.costUsd ?? 0;

    enforceTokenCap('daily', caps.dailyTokens, snapshot.daily.totalTokens, estTokens, refusalTemplate);
    enforceTokenCap('monthly', caps.monthlyTokens, snapshot.monthly.totalTokens, estTokens, refusalTemplate);

    if (caps.dailyUsd !== undefined) {
      enforceUsdCap('daily', caps.dailyUsd, snapshot.daily.costUsd, estUsd, refusalTemplate);
    }
    if (caps.monthlyUsd !== undefined) {
      enforceUsdCap('monthly', caps.monthlyUsd, snapshot.monthly.costUsd, estUsd, refusalTemplate);
    }
  }

  return {
    name: 'spend-tracker',

    async reload(): Promise<void> {
      caps = store.read(fallbackCaps).caps;
    },

    currentCaps(): SpendCapsState {
      return { ...caps };
    },

    usage(now: Date = new Date()) {
      const doc = store.read(fallbackCaps);
      return {
        daily: snapshotWithin(doc.entries, dailyPeriod, now),
        monthly: snapshotWithin(doc.entries, monthlyPeriod, now),
      };
    },

    async notify(event: LifecycleEvent): Promise<void> {
      if (event.kind === 'spend.preflight') {
        await store.withState(fallbackCaps, (doc) => {
          // Refresh caps inside the mutex so `caps set` writes that
          // happened between dispatches take effect on the next preflight.
          caps = { ...doc.caps };
          const snapshot = {
            daily: snapshotWithin(doc.entries, dailyPeriod, new Date()),
            monthly: snapshotWithin(doc.entries, monthlyPeriod, new Date()),
          };
          checkPreflight(snapshot, event.estimate);
        });
        return;
      }

      if (event.kind === 'executor.dispatch.completed') {
        const usage = event.response.usage;
        if (!usage) return;
        const inputTokens = usage.inputTokens ?? 0;
        const outputTokens = usage.outputTokens ?? 0;
        if (inputTokens === 0 && outputTokens === 0 && !usage.costUsd) {
          return;
        }
        await store.withState(fallbackCaps, (doc) => {
          doc.entries.push({
            at: new Date().toISOString(),
            jobId: event.jobId,
            inputTokens,
            outputTokens,
            costUsd: usage.costUsd,
          });
          // Trim entries older than the longest period (plus 24h grace).
          const horizonMs =
            (Math.max(
              dailyPeriod.durationHours ?? 24,
              monthlyPeriod.durationHours ?? 24 * 30,
            ) +
              24) *
            3600 *
            1000;
          const cutoff = Date.now() - horizonMs;
          doc.entries = doc.entries.filter(
            (e) => new Date(e.at).getTime() >= cutoff,
          );
          caps = { ...doc.caps };
        });
        return;
      }
    },
  };
}

function snapshotWithin(
  entries: { at: string; inputTokens: number; outputTokens: number; costUsd?: number }[],
  period: PeriodSpec,
  now: Date,
): UsageSnapshot {
  if (period.kind === 'calendar') {
    // Reserved; v0.1 behaves as 24h rolling until implemented.
    return rollingSnapshot(entries, 24, now);
  }
  return rollingSnapshot(entries, period.durationHours ?? 24, now);
}

function rollingSnapshot(
  entries: { at: string; inputTokens: number; outputTokens: number; costUsd?: number }[],
  hours: number,
  now: Date,
): UsageSnapshot {
  const cutoff = now.getTime() - hours * 3600 * 1000;
  let inputTokens = 0;
  let outputTokens = 0;
  let costUsd = 0;
  for (const entry of entries) {
    const t = new Date(entry.at).getTime();
    if (t < cutoff) continue;
    inputTokens += entry.inputTokens;
    outputTokens += entry.outputTokens;
    costUsd += entry.costUsd ?? 0;
  }
  return {
    inputTokens,
    outputTokens,
    totalTokens: inputTokens + outputTokens,
    costUsd,
  };
}

function enforceTokenCap(
  period: 'daily' | 'monthly',
  cap: number,
  used: number,
  estimate: number,
  template: string,
): void {
  if (used + estimate <= cap) return;
  const remaining = Math.max(0, cap - used);
  const message = renderTemplate(template, {
    cap: String(cap),
    period,
    used: String(used),
    remaining: String(remaining),
    unit: 'tokens',
  });
  // Sofia's SpendCapExceeded signature: (cap: string, used: number, limit: number)
  const err = new SpendCapExceeded(`${period}.tokens`, used, cap);
  (err as Error & { message: string }).message = message;
  throw err;
}

function enforceUsdCap(
  period: 'daily' | 'monthly',
  cap: number,
  used: number,
  estimate: number,
  template: string,
): void {
  if (used + estimate <= cap) return;
  const remaining = Math.max(0, cap - used);
  const message = renderTemplate(template, {
    cap: String(cap),
    period,
    used: String(used),
    remaining: String(remaining),
    unit: 'usd',
  });
  const err = new SpendCapExceeded(`${period}.usd`, used, cap);
  (err as Error & { message: string }).message = message;
  throw err;
}

// Re-export for callers that want to identify the throw shape.
export { SpendCapExceeded };
